"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { FUSportsLineupMember } from "fansunited-data-layer";
import type { PlayerEvents } from "./playerEventsHelper";
import { GoalIcon, YellowCardIcon, RedCardIcon, SubOutIcon, SubInIcon } from "@/components/ui/icons/PlayerEventIcons";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
const PLAYER_SILHOUETTE_URL = "/assets/img/avatar.webp";
interface MatchLineupFieldPlayerMarkerProps {
  player: FUSportsLineupMember;
  color: string;
  events?: PlayerEvents;
}

/**
 * Individual player marker on the lineup field
 * Displays a circular headshot (or shirt number if photos disabled) above the player's name badge
 * Shows event indicators for goals, cards, and substitutions positioned over the image
 */
export const MatchLineupFieldPlayerMarker: React.FC<MatchLineupFieldPlayerMarkerProps> = t0 => {
  const $ = _c(42);
  const {
    player,
    color,
    events
  } = t0;
  const {
    allowPlayerPhotos
  } = useTenantSettings();
  let t1;
  if ($[0] !== player.name) {
    t1 = player.name?.split(" ").pop() || player.name;
    $[0] = player.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const lastName = t1;
  const photoUrl = player.assets?.photoTransparent || player.assets?.photo || PLAYER_SILHOUETTE_URL;
  const nameBgColor = `${color}CC`;
  let leftIndicators;
  let rightIndicators;
  if ($[2] !== events) {
    leftIndicators = [];
    rightIndicators = [];
    if (events) {
      if (events.redCard) {
        let t2;
        if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = <RedCardIcon key="red" />;
          $[5] = t2;
        } else {
          t2 = $[5];
        }
        leftIndicators.push(t2);
      } else {
        if (events.yellowCard) {
          let t2;
          if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
            t2 = <YellowCardIcon key="yellow" />;
            $[6] = t2;
          } else {
            t2 = $[6];
          }
          leftIndicators.push(t2);
        }
      }
      if (events.substitutedOut) {
        let t2;
        if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = <SubOutIcon key="sub-out" />;
          $[7] = t2;
        } else {
          t2 = $[7];
        }
        leftIndicators.push(t2);
      } else {
        if (events.substitutedIn) {
          let t2;
          if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
            t2 = <SubInIcon key="sub-in" />;
            $[8] = t2;
          } else {
            t2 = $[8];
          }
          leftIndicators.push(t2);
        }
      }
      if (events.goals > 0) {
        let t2;
        if ($[9] !== events.goals) {
          t2 = <GoalIcon key="goal" count={events.goals} />;
          $[9] = events.goals;
          $[10] = t2;
        } else {
          t2 = $[10];
        }
        rightIndicators.push(t2);
      }
    }
    $[2] = events;
    $[3] = leftIndicators;
    $[4] = rightIndicators;
  } else {
    leftIndicators = $[3];
    rightIndicators = $[4];
  }
  let t2;
  if ($[11] !== color) {
    t2 = {
      backgroundColor: color
    };
    $[11] = color;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== allowPlayerPhotos || $[14] !== photoUrl || $[15] !== player.name || $[16] !== player.shirtNumber) {
    t3 = allowPlayerPhotos ? <img src={photoUrl} alt={player.name} className="w-full h-full object-cover rounded-full" /> : <div className="w-full h-full flex items-center justify-center text-white font-bold text-sm md:text-lg">{player.shirtNumber || "?"}</div>;
    $[13] = allowPlayerPhotos;
    $[14] = photoUrl;
    $[15] = player.name;
    $[16] = player.shirtNumber;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== leftIndicators) {
    t4 = leftIndicators.length > 0 && <div className="absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 flex items-center gap-0.5">{leftIndicators}</div>;
    $[18] = leftIndicators;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== rightIndicators) {
    t5 = rightIndicators.length > 0 && <div className="absolute bottom-0 right-0 translate-y-1/4 translate-x-1/4 flex items-center gap-0.5">{rightIndicators}</div>;
    $[20] = rightIndicators;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== t2 || $[23] !== t3 || $[24] !== t4 || $[25] !== t5) {
    t6 = <div className="w-8 h-8 md:w-12 md:h-12 rounded-full border border-white shadow-md mb-0.5 relative" style={t2}>{t3}{t4}{t5}</div>;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== nameBgColor) {
    t7 = {
      backgroundColor: nameBgColor
    };
    $[27] = nameBgColor;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== allowPlayerPhotos || $[30] !== color || $[31] !== player.shirtNumber) {
    t8 = allowPlayerPhotos && <div className="px-1 py-0.5 flex items-center justify-center min-w-4" style={{
      backgroundColor: color
    }}>{player.shirtNumber || "?"}</div>;
    $[29] = allowPlayerPhotos;
    $[30] = color;
    $[31] = player.shirtNumber;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== lastName) {
    t9 = <div className="px-1 py-0.5 max-w-16 truncate">{lastName}</div>;
    $[33] = lastName;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== t7 || $[36] !== t8 || $[37] !== t9) {
    t10 = <div className="flex items-center rounded overflow-hidden shadow-lg text-white text-[10px] font-semibold whitespace-nowrap" style={t7}>{t8}{t9}</div>;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== t10 || $[40] !== t6) {
    t11 = <div className="flex flex-col items-center">{t6}{t10}</div>;
    $[39] = t10;
    $[40] = t6;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  return t11;
};